import React, { useState, useRef, useEffect } from 'react';
import { Bell, CheckCheck, Trash2, X, Wifi, WifiOff } from 'lucide-react';
import { useNotifications, getNotificationIcon } from '../../contexts/NotificationContext';
import type { Notification } from '../../types/notifications';
import { useNavigate } from 'react-router-dom';
import Button from './Button';

const NotificationBell: React.FC = () => {
  const navigate = useNavigate();
  const { 
    notifications, 
    unreadCount, 
    isConnected, 
    deliveryError,
    markAsRead, 
    markAllAsRead, 
    clearNotifications,
    removeNotification
  } = useNotifications();

  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => { if (event.key === 'Escape') setIsOpen(false); };
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  const handleNotificationClick = (notification: Notification) => {
    markAsRead(notification.id);
    const eventId = notification.event_id || notification.data?.event_id;
    if (eventId) {
      navigate(`/events/${eventId}/view`);
      setIsOpen(false);
    }
  };

  const formatTime = (date: Date): string => {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    const diffHour = Math.floor(diffMin / 60);
    const diffDay = Math.floor(diffHour / 24);

    if (diffMin < 1) return 'Just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    if (diffHour < 24) return `${diffHour}h ago`;
    if (diffDay < 7) return `${diffDay}d ago`;
    
    return date.toLocaleDateString();
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Button */}
      <Button
        type="button"
        variant="ghost"
        aria-label="Notifications"
        aria-expanded={isOpen}
        onClick={() => setIsOpen(!isOpen)}
        className="relative"
        title={isConnected ? 'Notifications (Connected)' : 'Notifications (Disconnected)'}
      >
        <Bell size={20} className="text-text-secondary" />
        
        {/* Unread badge */}
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 flex items-center justify-center min-w-[18px] h-[18px] px-1 text-xs font-bold text-white bg-red-500 rounded-full">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}

        {/* Connection indicator */}
        <span 
          className={`absolute bottom-0.5 right-0.5 w-2.5 h-2.5 rounded-full border-2 border-background-primary ${
            isConnected ? 'bg-green-500' : 'bg-red-500'
          }`}
          title={isConnected ? 'Connected' : 'Disconnected'}
        />
      </Button>

      {/* Dropdown */}
      {isOpen && (
        <div className="fixed right-4 top-[60px] sm:absolute sm:top-auto sm:right-0 sm:mt-2 w-[min(384px,calc(100vw-32px))] max-h-[min(500px,75dvh)] bg-surface-primary border border-border-default rounded-xl shadow-xl z-50 overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between p-3 border-b border-surface-secondary bg-surface-secondary/50">
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-text-primary">Notifications</h3>
              <span className={`flex items-center gap-1 text-xs ${isConnected ? 'text-green-500' : 'text-red-500'}`}>
                {isConnected ? <Wifi size={12} /> : <WifiOff size={12} />}
                {isConnected ? 'Live' : 'Offline'}
              </span>
            </div>
            
            <div className="flex items-center gap-1">
              {unreadCount > 0 && (
                <button
                  onClick={markAllAsRead}
                  className="p-1.5 text-text-muted hover:text-text-primary hover:bg-surface-secondary rounded transition-colors"
                  title="Mark all as read"
                >
                  <CheckCheck size={16} />
                </button>
              )}
              {notifications.length > 0 && (
                <button
                  onClick={clearNotifications}
                  className="p-1.5 text-text-muted hover:text-red-500 hover:bg-surface-secondary rounded transition-colors"
                  title="Clear all"
                >
                  <Trash2 size={16} />
                </button>
              )}
            </div>
          </div>

          {/* Notification List */}
          {deliveryError && <p role="status" className="px-3 py-2 text-xs text-status-warning">{deliveryError}</p>}
          <div className="max-h-[400px] overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-text-muted">
                <Bell size={40} className="mb-3 opacity-50" />
                <p className="text-sm">No notifications</p>
              </div>
            ) : (
              <div className="divide-y divide-surface-secondary">
                {notifications.map((notification) => (
                  <div
                    key={notification.id}
                    role="button"
                    tabIndex={0}
                    onKeyDown={event => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); handleNotificationClick(notification); } }}
                    className={`group p-3 hover:bg-surface-secondary/50 cursor-pointer transition-colors ${
                      !notification.read ? 'bg-accent-primary/5' : ''
                    }`}
                    onClick={() => handleNotificationClick(notification)}
                  >
                    <div className="flex items-start gap-3">
                      {/* Icon */}
                      <div className="flex-shrink-0 text-lg">
                        {getNotificationIcon(notification.event_type)}
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <p className={`text-sm ${!notification.read ? 'font-semibold text-text-primary' : 'text-text-secondary'}`}>
                            {notification.title}
                          </p>
                          <button
                            aria-label={`Dismiss ${notification.title}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              removeNotification(notification.id);
                            }}
                            className="flex-shrink-0 p-0.5 text-text-muted hover:text-red-500 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 focus:opacity-100 transition-opacity"
                          >
                            <X size={14} />
                          </button>
                        </div>
                        <p className="text-xs text-text-muted mt-0.5 line-clamp-2">
                          {notification.body}
                        </p>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-xs text-text-muted">
                            {formatTime(notification.createdAt)}
                          </span>
                          {notification.data?.license_plate && (
                            <span className="px-1.5 py-0.5 text-xs font-mono bg-surface-secondary rounded">
                              {notification.data.license_plate}
                            </span>
                          )}
                          {!notification.read && (
                            <span className="w-2 h-2 rounded-full bg-accent-primary" />
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div className="p-2 border-t border-surface-secondary bg-surface-secondary/30">
              <p className="text-xs text-center text-text-muted">
                {notifications.length} notification{notifications.length !== 1 ? 's' : ''}
                {unreadCount > 0 && ` (${unreadCount} unread)`}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
